/**
 * The PURE presence status model — the one place that turns a
 * {@link UserPresence} row plus "now" into what the Online rail says about a
 * user. Shared (not portal-local) because the rail is the first of several
 * surfaces that will render presence (invites, friends, liked); a second
 * copy of these thresholds is exactly the drift this package exists to stop.
 *
 * The ladder, in order (first match wins):
 *
 *   fresh beat + a game       → `in-game`    "In game: Rotor Rescue"
 *   fresh beat                → `online`     "online"
 *   under an hour             → `minutes`    "last seen 12 minutes ago"
 *   earlier on the viewer's today → `hours`  "last seen 5 hours ago"
 *   the viewer's yesterday    → `yesterday`  "last seen yesterday"
 *   anything older            → `hidden`     (not listed — owner's rule)
 *
 * "Today" and "yesterday" are LOCAL-CALENDAR days on the viewer's clock, which
 * is why the cut lives here and not in the RLS policy (0040 bounds reads to a
 * physical 48h retention window; this is the display rule inside it). The
 * minutes rung deliberately outranks the calendar rungs so a beat 40 minutes
 * before midnight reads "40 minutes ago" at 00:20, not "yesterday".
 */
import type { UserPresence } from '../types.js';

/** How often a runtime beats presence (portal SPA, natives, creations). */
export const PRESENCE_HEARTBEAT_MS = 60_000;

/**
 * How long a visible runtime may keep beating without any human input.
 * Visibility alone is not presence: a tablet, PWA, or browser window can stay
 * foregrounded for days after its owner walks away. Input after this cutoff
 * wakes the beat immediately.
 */
export const PRESENCE_IDLE_MS = 300_000;

/**
 * How long a beat keeps a user "online". 2.5× the heartbeat, so a single
 * dropped beat (a slow network, a backgrounded tab about to resume) never
 * flickers someone offline; two consecutive misses do.
 */
export const PRESENCE_ONLINE_MS = 150_000;

/** Physical read window of the 0040 RLS policy — mirrored here so callers can
 *  reason about the outer bound without reading SQL. Display never reaches it
 *  (the `yesterday` rung cuts first); it is the archive bound, not the rule. */
export const PRESENCE_RETENTION_MS = 172_800_000; // 48h

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;

/** What the rail says about one user. */
export type PresenceStatus =
  | { kind: 'in-game'; gameTitle: string; gameSlug: string | null }
  | { kind: 'online' }
  | { kind: 'minutes'; minutes: number }
  | { kind: 'hours'; hours: number }
  | { kind: 'yesterday' }
  | { kind: 'hidden' };

/** Local-calendar midnight at or before `ms`, on the viewer's clock. */
function startOfLocalDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Derive a user's status from their row and the viewer's clock. Pure: every
 * boundary is a function of (`lastSeenAt`, `now`) alone, so the rail can
 * re-derive on a timer without refetching.
 *
 * A `gameKey` only reads as "In game" while the beat is FRESH — a user who
 * closed the tab mid-game leaves a stale row whose last state was a game, and
 * claiming they are still playing it an hour later is the one lie this model
 * must not tell.
 */
export function presenceStatus(row: UserPresence, now: number): PresenceStatus {
  const age = now - row.lastSeenAt;

  // A clock-skewed future beat is still a live one.
  if (age <= PRESENCE_ONLINE_MS) {
    return row.gameKey && row.gameTitle
      ? { kind: 'in-game', gameTitle: row.gameTitle, gameSlug: row.gameSlug ?? null }
      : { kind: 'online' };
  }
  if (age < HOUR_MS) {
    return { kind: 'minutes', minutes: Math.max(1, Math.round(age / MINUTE_MS)) };
  }

  const today = startOfLocalDay(now);
  if (row.lastSeenAt >= today) {
    return { kind: 'hours', hours: Math.max(1, Math.floor(age / HOUR_MS)) };
  }
  if (row.lastSeenAt >= startOfLocalDay(today - 1)) return { kind: 'yesterday' };
  return { kind: 'hidden' };
}

/** The rail's line for a status. `hidden` has no line — callers drop it first. */
export function formatPresenceStatus(status: PresenceStatus): string {
  switch (status.kind) {
    case 'in-game':
      return `In game: ${status.gameTitle}`;
    case 'online':
      return 'online';
    case 'minutes':
      return `last seen ${status.minutes} ${status.minutes === 1 ? 'minute' : 'minutes'} ago`;
    case 'hours':
      return `last seen ${status.hours} ${status.hours === 1 ? 'hour' : 'hours'} ago`;
    case 'yesterday':
      return 'last seen yesterday';
    case 'hidden':
      return '';
  }
}

/** Sort rank — in-game first (the most interesting thing on the rail), then
 *  online, then by recency. */
const RANK: Record<PresenceStatus['kind'], number> = {
  'in-game': 0,
  online: 1,
  minutes: 2,
  hours: 3,
  yesterday: 4,
  hidden: 5,
};

/** A row paired with the status derived for it. */
export interface PresenceEntry {
  row: UserPresence & { id: string };
  status: PresenceStatus;
}

/**
 * The rail's list: drop everyone last seen before yesterday, order in-game →
 * online → most recent. Ties break on handle so the order is stable across
 * re-derives (two users beaten in the same second must not swap places).
 */
export function visiblePresence(
  rows: ReadonlyArray<UserPresence & { id: string }>,
  now: number,
): PresenceEntry[] {
  return rows
    .map((row) => ({ row, status: presenceStatus(row, now) }))
    .filter((e) => e.status.kind !== 'hidden')
    .sort(
      (a, b) =>
        RANK[a.status.kind] - RANK[b.status.kind] ||
        b.row.lastSeenAt - a.row.lastSeenAt ||
        a.row.handle.localeCompare(b.row.handle),
    );
}
